import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Nahrání souboru krok za krokem',
    summary: 'Jak nahrát CSV a přečíst výsledek ověření.',
    body: (
      <>
        <p>Jakmile je váš soubor CSV připraven, nahrajte ho z prostředí komunity:</p>
        <ol>
          <li>Otevřete svou komunitu a v levém menu zvolte <strong>Správa dat</strong>.</li>
          <li>V části <strong>Vybrat typ datové sady</strong> zvolte typ souboru, který nahráváte
            (například „Building Load Profile").</li>
          <li>Pokud budete vyzváni, zvolte <strong>budovu</strong>, ke které soubor patří.</li>
          <li>Přetáhněte soubor CSV do oblasti pro nahrávání nebo klikněte na <strong>Procházet soubory</strong>.</li>
          <li>Klikněte na <strong>Ověřit &amp; Náhled</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="Stránka Správa dat: vyberte typ, poté přetáhněte soubor."
        />
        <h5 className="mt-4">Čtení výsledku</h5>
        <ul>
          <li><strong>Zelený banner</strong> — soubor je platný. Zobrazuje počet načtených řádků a rozsah dat. Máte hotovo.</li>
          <li><strong>Žlutá upozornění</strong> — soubor byl přijat, ale je vhodné něco zkontrolovat (například chybějící hodiny). Soubor můžete ponechat nebo vylepšit.</li>
          <li><strong>Červený banner</strong> — soubor byl odmítnut. Náhled uvádí každý problém s číslem řádku. Opravte soubor a nahrajte ho znovu.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="Úspěšné nahrání zobrazí zelený banner a náhled."
        />
        <p>
          Každá červená chybová zpráva je vysvětlena v článku
          <em> „Proč nahrávání selhalo?"</em> v sekci Řešení problémů.
        </p>
      </>
    ),
  },

  {
    id: 'data-load',
    category: 'data',
    title: 'Soubory spotřeby budovy (zatížení)',
    summary: 'Hodinový soubor spotřeby elektřiny — nejdůležitější soubor pro každou budovu.',
    body: (
      <>
        <p>
          <strong>Building Load Profile</strong> zaznamenává, kolik elektřiny budova spotřebovala,
          jedna hodnota za hodinu. Je základem pro prognózování a optimalizaci, proto ho připravte
          pro každou budovu, pokud je to možné.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Povinný?</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Povinný</td><td>Hodina, např. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Povinný</td><td>Spotřeba elektřiny v dané hodině v kWh (číslo, desetinná tečka, nikdy záporné)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Volitelný</td><td>Nejvyšší odběr výkonu v dané hodině, v kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Příklad:</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>Jedna hodnota za hodinu; celý rok tvoří 8 760 řádků. Potřebujete alespoň 24 řádků (jeden den).</p>
        <TemplateBtn type="building_load_profile">Stáhnout šablonu zatížení</TemplateBtn>
        <Warn>Spotřeba nesmí být nikdy záporná. Záporná hodnota znamená, že sloupce byly zaměněny — zkontrolujte, zda výroba nebyla vložena do sloupce spotřeby.</Warn>
      </>
    ),
  },

  {
    id: 'data-generation',
    category: 'data',
    title: 'Soubory výroby ze solárních a větrných zdrojů',
    summary: 'Hodinová výroba solárních panelů a větrných turbín.',
    body: (
      <>
        <p>Soubory výroby zaznamenávají, kolik energie obnovitelný zdroj vyrobil, jedna hodnota za hodinu.</p>
        <h5 className="mt-3">Komunitní solární elektrárna nebo větrná turbína</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Význam</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Hodina, např. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>Výkon vyrobený v dané hodině, v kW (nikdy záporný — v noci je solární výroba <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Šablona solární (FV)</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Šablona větrná</TemplateBtn>

        <h5 className="mt-4">Vlastní střešní solární panely budovy</h5>
        <p>
          Pro solární výrobu náležející jedné budově je hodnotový sloupec
          <Code>generation_kwh</Code> (energie v kWh) místo <Code>generation_kw</Code>.
          Platforma zobrazuje přesný očekávaný název sloupce, když na stránce nahrávání vyberete typ datové sady
          — vždy se řiďte tím, co je tam zobrazeno.
        </p>
        <Tip>Hodiny bez slunce nebo větru by měly obsahovat <Code>0</Code>, ne být prázdné.</Tip>
      </>
    ),
  },

  {
    id: 'data-tariff',
    category: 'data',
    title: 'Soubory tarifu (ceny elektřiny)',
    summary: 'Ceny elektřiny pro odběr a dodávku — historické i Day-Ahead.',
    body: (
      <>
        <p>Soubory tarifu sdělují platformě cenu elektřiny pro každou hodinu.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Soubor</th><th>Hodnotový sloupec</th><th>Řádky</th></tr>
          </thead>
          <tbody>
            <tr><td>Historický tarif odběru</td><td><Code>import_price_eur_kwh</Code></td><td>Jeden rok, hodinově (≈8 760)</td></tr>
            <tr><td>Historický tarif dodávky</td><td><Code>export_price_eur_kwh</Code></td><td>Jeden rok, hodinově (≈8 760)</td></tr>
            <tr><td>Day-Ahead tarif odběru</td><td><Code>import_price_eur_kwh</Code></td><td>24 (jeden den)</td></tr>
            <tr><td>Day-Ahead tarif dodávky</td><td><Code>export_price_eur_kwh</Code></td><td>24 (jeden den)</td></tr>
          </tbody>
        </Table>
        <p>
          Cena <strong>odběru</strong> je to, co platíte za nákup elektřiny; cena <strong>dodávky</strong>
          je to, co dostanete za prodej. Ceny jsou v eurech za kWh (např. <Code>0.22</Code>)
          a používají desetinnou tečku.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Šablona tarifu odběru</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Šablona tarifu dodávky</TemplateBtn>
        <Tip>
          Soubory <strong>Day-Ahead</strong> pokrývají pouze jeden den (24 řádků). Nahrajte nový každý den —
          jeho časové značky by měly odpovídat datu daného dne. Výše uvedené šablony slouží také jako
          referenční formát pro 24 řádků.
        </Tip>

        <h5 className="mt-4">Kde mohu získat data o tarifech?</h5>
        <p>
          Pod pojmem „tarif" se skrývají dvě různé věci — zacházejte s nimi odděleně:
        </p>
        <ul>
          <li><strong>Velkoobchodní / Day-Ahead ceny</strong> — hodinová tržní cena zveřejňovaná
            burzou elektřiny vaší země. Použijte ji pro soubory tarifu <em>Day-Ahead</em> a jako
            přiblížení pro variabilní smlouvy.</li>
          <li><strong>Maloobchodní tarif</strong> — co skutečně platíte (energie + síťové poplatky + daně).
            Vezměte tuto hodnotu ze smlouvy nebo faktury od dodavatele.</li>
        </ul>

        <p className="mt-3"><strong>Celoevropský zdroj (funguje pro všechny země):</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Zdarma, ale
            vyžaduje bezplatný účet. Otevřete <em>Market → Day-ahead Prices</em>, vyberte svou nabídkovou
            zónu (např. IT-North, FR, DE-LU, CZ, GR), rozsah dat a <em>Export Data</em> → CSV.
            Cenový sloupec je v <strong>€/MWh — vydělte 1 000 pro převod na €/kWh</strong>
            (např. 220 €/MWh = 0,22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>Národní zdroje (často jednodušší, bez nutnosti účtu):</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Země</th><th>Stránka</th><th>Co získáte</th></tr>
          </thead>
          <tbody>
            <tr><td>Itálie</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>Cena PUN Day-Ahead, hodinově. Hledejte „Esiti MGP". €/MWh — vydělte 1 000.</td></tr>
            <tr><td>Francie</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) nebo <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE: bezplatný portál otevřených dat, hodinové ceny Day-Ahead. EPEX: tržní portál.</td></tr>
            <tr><td>Německo</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Spolková síťová agentura)</td><td>„Market data" → „Wholesale prices" → „Day-ahead, Germany/Luxembourg". Volné stažení, bez účtu.</td></tr>
            <tr><td>Česká rep.</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>„Short-term markets" → „Day-ahead market". Export CSV po dnech.</td></tr>
            <tr><td>Řecko</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Výsledky Day-Ahead trhu, denní PDF a CSV. €/MWh — vydělte 1 000.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Převod staženého cenového souboru do formátu platformy</h5>
        <p>
          Exporty z burz elektřiny málokdy mají přesné názvy sloupců, které platforma vyžaduje. Pomocí
          těchto kroků převeďte kterýkoli z výše uvedených souborů:
        </p>
        <ol>
          <li>Otevřete stažené CSV v Excel nebo Google Sheets.</li>
          <li>Identifikujte sloupec s datem/časem a sloupec s cenou (ostatní sloupce lze smazat).</li>
          <li>Vytvořte sloupec s názvem <Code>timestamp</Code>. Pokud zdroj již obsahuje časovou značku ISO,
            zkopírujte ji; jinak ji sestavte pomocí vzorce z článku
            {' '}<ArticleLink to="data-csv-basics">Správné vytváření CSV souborů</ArticleLink>.</li>
          <li>Vytvořte sloupec s názvem <Code>import_price_eur_kwh</Code>. Pokud je zdroj v €/MWh, vydělte
            hodnotu 1 000 vzorcem například <Code>=B2/1000</Code> a vložte jako hodnoty.</li>
          <li>Uložte jako <strong>CSV UTF-8</strong> a nahrajte jako <em>Historical Import Tariff</em>.</li>
        </ol>

        <Warn>
          Velkoobchodní cena ≠ maloobchodní cena. Velkoobchodní hodnota je pouze tržní cena energie a
          nezahrnuje síťové poplatky, odvody ani DPH. Pro přesnou analýzu nákladů preferujte maloobchodní
          tarif od vašeho dodavatele, nebo kombinujte velkoobchodní cenu se známou fixní přirážkou.
        </Warn>

        <h5 className="mt-4">Tarif dodávky (výkupní cena)</h5>
        <p>
          Ceny dodávky závisí na národním schématu — mohou to být paušální výkupní tarify, velkoobchodní
          cena minus sleva nebo nula. Zjistěte pravidla od svého dodavatele, provozovatele distribuční
          soustavy (DSO) nebo národního regulátora (ARERA v Itálii, CRE ve Francii, BNetzA v Německu,
          ERÚ v Česku, RAE v Řecku) a pro každou hodinu zadejte výslednou hodnotu v €/kWh.
        </p>
      </>
    ),
  },
]
